<template>
  <LayoutSection
    v-if="props.video?.url || isEditing"
    :id="id"
    :bg-white="style === 'white'"
    :bg-blue="style === 'blue'"
    :bg-primary-light="style === 'greenLight'"
    padding
    class="mb-30 paragraph--padded"
  >
    <h2
      v-if="title && title.trim().length > 0"
      :id="headerId"
      class="text-2xl font-bold mb-30"
      :class="{ 'text-white': isInverted }"
    >
      {{ title }}
    </h2>

    <div
      v-if="contextDescription"
      class="mb-30 ck-content text-base font-normal"
      :class="{ 'text-white': isInverted }"
      v-html="contextDescription"
    />

    <div
      v-if="signLanguageVideo"
      class="mb-10"
      :class="{ 'text-white': isInverted }"
    >
      <ToggleSwitch
        :id="`${id}_sign_lang_toggle`"
        v-model="isSignLanguageVideo"
        :is-on-dark="isInverted"
      >
        {{ $texts('video.signLangVideo', 'Video in Gebärdensprache') }}
        <SpriteSymbol name="hands" class="size-40" aria-hidden="true" />
      </ToggleSwitch>
    </div>

    <figure>
      <iframe
        v-if="embedUrl && isPlaying"
        :src="embedUrl"
        :title="title"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
        class="aspect-video w-full md:px-40 lg:px-0"
      />

      <button
        v-if="!isPlaying"
        class="aspect-video w-full md:px-40 lg:px-0 cursor-pointer flex items-center justify-center relative"
        :aria-label="$texts('video.load', 'Video laden')"
        @click.prevent="isPlaying = true"
      >
        <!-- Using VuepalRokkaImage directly here to avoid nested <figure> -->
        <FieldItemTypeImage
          v-if="video?.thumbnailCustom"
          v-bind="video?.thumbnailCustom"
          config="header"
          img-class="absolute top-0 left-0 w-full h-full"
        />
        <FieldItemTypeImage
          v-else-if="video?.thumbnailOriginal"
          v-bind="video?.thumbnailOriginal"
          config="header"
          img-class="absolute top-0 left-0 w-full h-full object-cover"
        />

        <SpriteSymbol name="play" class="size-[75px] z-10" aria-hidden="true" />
      </button>

      <figcaption
        v-if="videoDescription || source"
        class="flex w-full justify-between mt-10"
      >
        <div v-if="videoDescription" :class="{ 'text-white': isInverted }">
          {{ videoDescription }}
        </div>
        <div
          v-if="source"
          class="flex-1 text-right"
          :class="style === 'blue' ? 'text-gray-300' : 'text-gray-700'"
        >
          &copy; {{ source }}
        </div>
      </figcaption>
    </figure>
  </LayoutSection>
</template>

<script lang="ts" setup>
import type { ParagraphVideoRemoteFragment } from '#graphql-operations'
import {
  getYouTubeDetails,
  getVimeoDetails,
  type VideoDetails,
} from '~/helpers/video'
import { slugify } from '~/helpers/slugify'

enum VideoSourcePlatform {
  YOUTUBE = 'youtube',
  VIMEO = 'vimeo',
}

const { options, isEditing } = defineBlokkli({
  bundle: 'video_remote',
  globalOptions: ['showInToc', 'bkVisibleLanguages'],
  chunkName: 'rare',
  options: {
    style: {
      type: 'radios',
      label: 'Farbe',
      default: 'white',
      displayAs: 'colors',
      options: {
        white: { class: 'bg-white', label: 'Weiss' },
        greenLight: { class: 'bg-primary-100', label: 'Hellgrün' },
        blue: { class: 'bg-blue-800', label: 'Blau' },
      },
    },
  },
  editor: {
    editTitle: (el) => el.querySelector('h2')?.innerText,
  },
})

const style = computed(() => options.value.style)
const isInverted = computed(() => options.value.style === 'blue')

const props = defineProps<{
  title?: ParagraphVideoRemoteFragment['title']
  video?: ParagraphVideoRemoteFragment['video']
  signLanguageVideo?: ParagraphVideoRemoteFragment['signLanguageVideo']
  videoDescription?: ParagraphVideoRemoteFragment['videoDescription']
  contextDescription?: ParagraphVideoRemoteFragment['contextDescription']
  source?: ParagraphVideoRemoteFragment['source']
}>()

const id = useId()
const headerId = computed(() =>
  props.title ? slugify(props.title) : undefined,
)
const isSignLanguageVideo = ref(false)
const isPlaying = ref(false)

watch(isSignLanguageVideo, () => {
  isPlaying.value = false
})

const kind = computed<VideoSourcePlatform | null>(() => {
  // Matching `youtu` for youtube.com and youtu.be
  if (props.video?.url?.toLowerCase().includes('youtu')) {
    return VideoSourcePlatform.YOUTUBE
  }

  if (props.video?.url?.toLowerCase().includes('vimeo')) {
    return VideoSourcePlatform.VIMEO
  }

  if (isEditing) {
    throw new Error('Unknown video source type for video URL')
  }

  return null
})

const videoDetails = computed<VideoDetails | null>(() => {
  let video = props.video
  if (props.signLanguageVideo && isSignLanguageVideo.value) {
    video = props.signLanguageVideo
  }

  if (!video?.url) {
    return null
  }

  if (kind.value === VideoSourcePlatform.YOUTUBE) {
    return getYouTubeDetails(video.url)
  }

  if (kind.value === VideoSourcePlatform.VIMEO) {
    return getVimeoDetails(video.url)
  }

  if (isEditing) {
    throw new Error(
      "Don't know how to get ID and/or hash from video: " + video.url,
    )
  }

  return null
})

const embedUrl = computed(() => {
  if (!props.video?.url) {
    return null
  }

  if (!videoDetails.value?.id) {
    return null
  }

  if (kind.value === VideoSourcePlatform.YOUTUBE) {
    return `https://www.youtube-nocookie.com/embed/${videoDetails.value.id}?modestbranding=1&rel=0&autoplay=1`
  }

  if (kind.value === VideoSourcePlatform.VIMEO) {
    if (videoDetails.value.hash) {
      return `https://player.vimeo.com/video/${videoDetails.value.id}?h=${videoDetails.value.hash}&autoplay=1`
    }

    return `https://player.vimeo.com/video/${videoDetails.value.id}?autoplay=1`
  }
  return ''
})

defineOptions({
  name: 'ParagraphVideoRemote',
})
</script>

<style lang="postcss">
@import '~/assets/css/components/form-elements/toggle-switch.css';
</style>
